import React, { useEffect, useRef, useState } from "react";
import { Breadcrumb, Tag, message } from "antd";
import Loader from "../../components/loader/loader";
import { callApi } from "../../api/apiCaller";
import { routes } from "../../api/routes";
import { GreenNotify, RedNotify } from "../../helper/helper";
import "./uploadBulkOffers.css";

const FILE_NAME_PATTERN = /^([A-Za-z0-9-]+)_(EN|AR)_(\d{8})_(\d{4})(?:\.[^.]+)?$/i;
const NOOP = () => {};
const DEFAULT_EMPLOYER_IMAGE =
  "https://novathreadbucket.s3.amazonaws.com/nova-app-1685176470232-dummy.PNG";

const normalizeLookupKey = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");

const formatFileSize = (bytes) => {
  if (bytes === 0) {
    return "0 B";
  }

  if (!bytes) {
    return "";
  }

  if (bytes < 1024) {
    return `${bytes} B`;
  }

  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatTimestamp = (dateRaw, timeRaw) => {
  if (!dateRaw || !timeRaw) {
    return "Unknown";
  }

  const year = dateRaw.slice(0, 4);
  const month = dateRaw.slice(4, 6);
  const day = dateRaw.slice(6, 8);
  const hour = timeRaw.slice(0, 2);
  const minute = timeRaw.slice(2, 4);

  return `${year}-${month}-${day} ${hour}:${minute}`;
};

const parseFileName = (fileName) => {
  const baseName = String(fileName ?? "").replace(/\.[^.]+$/, "");
  const match = baseName.match(FILE_NAME_PATTERN);

  if (!match) {
    return {
      valid: false,
      error:
        "Filename must look like AHLI_AR_20260527_1052.xlsx or SAIB_EN_20260527_1110.xlsx.",
    };
  }

  const [, employerCodeRaw, languageCodeRaw, dateRaw, timeRaw] = match;
  const languageCode = languageCodeRaw.toUpperCase();
  const language = languageCode === "EN" ? "english" : "arabic";

  return {
    valid: true,
    employerCode: employerCodeRaw.toUpperCase(),
    languageCode,
    language,
    dateRaw,
    timeRaw,
  };
};

const BulkOfferUpload = () => {
  const fileInputRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [employers, setEmployers] = useState([]);
  const [employerLoadError, setEmployerLoadError] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  useEffect(() => {
    const getAllEmployers = () => {
      const getRes = (res) => {
        setEmployers(res?.data?.data || []);
        setEmployerLoadError(false);
      };

      callApi(
        "GET",
        routes.getAllEmployers,
        null,
        setIsLoading,
        getRes,
        (error) => {
          setEmployerLoadError(true);
          RedNotify(error?.message || "Unable to load employer codes.");
        }
      );
    };

    getAllEmployers();
  }, []);

  const isSupportedFile = (file) => {
    const name = file?.name?.toLowerCase() || "";
    return name.endsWith(".xlsx") || name.endsWith(".xls") || name.endsWith(".csv");
  };

  const getFileKey = (file) =>
    `${file.name}-${file.size}-${file.lastModified}`;

  const findEmployerByCode = (code) => {
    const normalizedCode = normalizeLookupKey(code);

    if (!normalizedCode) {
      return null;
    }

    return (
      employers.find((employer) => {
        const candidateValues = [
          employer?.employerCode,
          employer?.code,
          employer?.shortCode,
        ]
          .filter(Boolean)
          .map(normalizeLookupKey);

        return candidateValues.some((candidate) => candidate === normalizedCode);
      }) || null
    );
  };

  const inspectFile = async (file) => {
    const parsedName = parseFileName(file.name);

    if (!parsedName.valid) {
      return {
        id: getFileKey(file),
        file,
        name: file.name,
        size: file.size,
        status: "error",
        error: parsedName.error,
      };
    }

    const employer = findEmployerByCode(parsedName.employerCode);

    if (!employer) {
      return {
        id: getFileKey(file),
        file,
        name: file.name,
        size: file.size,
        status: "error",
        error: `Employer code "${parsedName.employerCode}" was not found in the system.`,
      };
    }

    const languageLabel = parsedName.language === "english" ? "English" : "Arabic";
    const employerDisplayName = employer?.name || parsedName.employerCode;
    const employerImage = employer?.image || DEFAULT_EMPLOYER_IMAGE;

    return {
      id: getFileKey(file),
      file,
      name: file.name,
      size: file.size,
      status: "ready",
      error: "",
      employerId: employer._id,
      employerCode: parsedName.employerCode,
      employerName: employerDisplayName,
      employerImage,
      language: parsedName.language,
      languageCode: parsedName.languageCode,
      languageLabel,
      fileStamp: formatTimestamp(parsedName.dateRaw, parsedName.timeRaw),
      batchKey: `${employer._id}-${parsedName.language}`,
      batchLabel: `${parsedName.employerCode} · ${languageLabel}`,
    };
  };

  const handleFiles = async (incomingFiles) => {
    const files = Array.from(incomingFiles || []);

    if (employerLoadError) {
      RedNotify("Employer codes are not loaded yet. Please refresh the page.");
      return;
    }

    const supportedFiles = files.filter(isSupportedFile);
    if (!supportedFiles.length) {
      RedNotify("Please upload only .xlsx, .xls, or .csv files.");
      return;
    }

    const existingKeys = new Set(selectedFiles.map((item) => item.id));
    const freshFiles = [];
    const duplicateNames = [];

    supportedFiles.forEach((file) => {
      const key = getFileKey(file);
      if (existingKeys.has(key)) {
        duplicateNames.push(file.name);
        return;
      }

      freshFiles.push(file);
    });

    if (duplicateNames.length) {
      message.warning(`${duplicateNames.length} duplicate file(s) were skipped.`);
    }

    if (!freshFiles.length) {
      return;
    }

    const inspectedFiles = await Promise.all(
      freshFiles.map(async (file) => inspectFile(file))
    );

    setSelectedFiles((current) => [...current, ...inspectedFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileChange = (event) => {
    handleFiles(event.target.files);
  };

  const handleBrowseClick = () => {
    if (isLoading) {
      return;
    }

    if (employerLoadError) {
      RedNotify("Employer codes are still unavailable. Please refresh first.");
      return;
    }

    fileInputRef.current?.click();
  };

  const handleDrop = (event) => {
    event.preventDefault();

    if (isLoading || employerLoadError) {
      return;
    }

    handleFiles(event.dataTransfer.files);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
  };

  const removeFile = (fileId) => {
    setSelectedFiles((current) => current.filter((item) => item.id !== fileId));
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const readyFiles = selectedFiles.filter((item) => item.status === "ready");
  const invalidFiles = selectedFiles.filter((item) => item.status !== "ready");

  const batchSummaries = readyFiles.reduce((groups, file) => {
    const existingBatch = groups.find((item) => item.batchKey === file.batchKey);

    if (existingBatch) {
      existingBatch.files.push(file);
      existingBatch.fileCount += 1;
      return groups;
    }

    groups.push({
      batchKey: file.batchKey,
      employerId: file.employerId,
      employerCode: file.employerCode,
      employerName: file.employerName,
      employerImage: file.employerImage,
      language: file.language,
      languageLabel: file.languageLabel,
      batchLabel: file.batchLabel,
      files: [file],
      fileCount: 1,
    });

    return groups;
  }, []);

  const totalFiles = readyFiles.length;
  const canUpload = readyFiles.length > 0 && invalidFiles.length === 0 && !isLoading;

  const handleUpload = async () => {
    if (!readyFiles.length) {
      RedNotify("Please add at least one valid Excel file.");
      return;
    }

    if (invalidFiles.length) {
      RedNotify("Please remove invalid files before uploading.");
      return;
    }

    if (!batchSummaries.length) {
      RedNotify("No valid batches are ready to upload.");
      return;
    }

    setIsLoading(true);

    try {
      const uploadPayload = new FormData();

      batchSummaries.forEach((batch) => {
        batch.files.forEach((item) => {
          uploadPayload.append("files", item.file);
        });
      });

      const response = await callApi(
        "POST",
        routes.uploadBulkOffers,
        uploadPayload,
        NOOP,
        NOOP,
        NOOP,
        0,
        true
      );

      if (!response) {
        throw new Error("Bulk upload failed.");
      }

      if (typeof response.status === "number" && response.status >= 400) {
        throw new Error(response.message || "Bulk upload failed.");
      }

      if (response.success === false) {
        throw new Error(response.message || "Bulk upload failed.");
      }

      GreenNotify(
        `Uploaded ${response?.data?.totalBatches || batchSummaries.length} batch(s) and ${
          response?.data?.totalRows || 0
        } offer row(s) in one request successfully.`
      );
      clearFiles();
    } catch (error) {
      RedNotify(error?.message || "Upload failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="modern-page-container">
      <Loader loading={isLoading} />

      <div className="page-header">
        <div className="page-header-content">
          <div>
            <Breadcrumb
              separator="›"
              className="modern-breadcrumb"
              items={[
                { title: "Dashboard" },
                {
                  title: (
                    <span className="breadcrumb-active">Bulk Offer Upload</span>
                  ),
                },
              ]}
            />
            <h1 className="page-title">Bulk Offer Upload</h1>
            <p className="page-subtitle">
              Drop multiple Excel files and let the filename route each batch to the
              correct employer and language automatically.
            </p>
          </div>
          {/* <button
            onClick={() => navigate("/offerEng")}
            className="modern-back-btn"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19 12H5M5 12L12 19M5 12L12 5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Back to Offers
          </button> */}
        </div>
      </div>

      {employerLoadError && (
        <div className="bulk-upload-alert bulk-upload-alert-error">
          Employer codes could not be loaded. Refresh the page before adding files.
        </div>
      )}

      <div className="bulk-upload-format-card">
        <div className="bulk-upload-format-copy">
          <p className="bulk-upload-kicker">Filename rules</p>
          <h2>Use this exact pattern</h2>
          <p>
            The system reads the employer code and language directly from the file
            name, so no manual dropdowns are needed.
          </p>
        </div>

        <div className="bulk-upload-format-examples">
          <div className="bulk-upload-sample">
            <code>AHLI_AR_20260527_1052.xlsx</code>
            <span>AHLI = employer code, AR = Arabic</span>
          </div>
          <div className="bulk-upload-sample">
            <code>SAIB_EN_20260527_1110.xlsx</code>
            <span>SAIB = employer code, EN = English</span>
          </div>
        </div>
      </div>

      <div className="stats-container">
        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M4 20V4H20V20H4Z"
                stroke="white"
                strokeWidth="2"
                strokeLinejoin="round"
              />
              <path
                d="M8 9H16M8 13H16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Files Added</p>
            <h3 className="stat-value">{selectedFiles.length}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ background: "linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)" }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 7H4C2.89543 7 2 7.89543 2 9V19C2 20.1046 2.89543 21 4 21H18C19.1046 21 20 20.1046 20 19V9C20 7.89543 19.1046 7 18 7H16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path
                d="M8 5H14V9H8V5Z"
                stroke="white"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Ready Batches</p>
            <h3 className="stat-value">{batchSummaries.length}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2" />
              <path
                d="M12 8V12L15 14"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Ready Files</p>
            <h3 className="stat-value">{readyFiles.length}</h3>
          </div>
        </div>

        <div className="stat-card">
          <div
            className="stat-icon"
            style={{ background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 7V17M7 12H17"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="stat-info">
            <p className="stat-label">Invalid Files</p>
            <h3 className="stat-value">{invalidFiles.length}</h3>
          </div>
        </div>
      </div>

      <div className="form-card">
        <div className="form-header">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <h2 className="form-title" style={{ margin: 0 }}>
              Direct Upload
            </h2>
            <Tag color="blue" style={{ marginRight: 0 }}>
              {batchSummaries.length || 0} batch(s) ready
            </Tag>
          </div>
          <p className="form-description">
            Each file is checked against the filename pattern, employer codes, and
            language content before upload. All ready batches are saved in one
            request.
          </p>
        </div>

        <div className="form-content">
          <div className="upload-steps">
            <div className="upload-step completed">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3 className="step-title">Add Files</h3>
                <p className="step-description">
                  Select one or more Excel files or drag them into the upload area.
                </p>
                <div
                  className={`bulk-file-picker ${
                    isLoading || employerLoadError ? "disabled" : ""
                  }`}
                  onClick={handleBrowseClick}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleBrowseClick();
                    }
                  }}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  role="button"
                  aria-disabled={isLoading || employerLoadError}
                  aria-label="Add bulk offer files"
                  tabIndex={0}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    multiple
                    onChange={handleFileChange}
                    className="file-input-hidden"
                  />
                  <div className="bulk-file-picker-icon">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 8L12 3L7 8"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 3V15"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <span className="bulk-file-picker-title">
                    Click to browse or drop files here
                  </span>
                  <span className="bulk-file-picker-subtitle">
                    Supported formats: .xlsx, .xls, .csv
                  </span>
                  <span className="bulk-file-picker-hint">
                    Filenames must include employer code and language.
                  </span>
                </div>
              </div>
            </div>

            <div className="upload-step completed">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3 className="step-title">Review Files</h3>
                <p className="step-description">
                  Check the parsed employer code, language, and batch grouping below.
                </p>

                <div className="selected-files-card">
                  <div className="selected-files-header">
                    <div>
                      <h3 className="selected-files-title">Selected Files</h3>
                      <p className="selected-files-subtitle">
                        {selectedFiles.length
                          ? "Each file is validated before it can be uploaded."
                          : "No files added yet."}
                      </p>
                    </div>
                    <div className="selected-files-tags">
                      <Tag color="blue">{selectedFiles.length} file(s)</Tag>
                      <Tag color="green">{readyFiles.length} ready</Tag>
                      {invalidFiles.length > 0 && (
                        <Tag color="red">{invalidFiles.length} invalid</Tag>
                      )}
                    </div>
                  </div>

                  {selectedFiles.length ? (
                    <ul className="selected-files-list">
                      {selectedFiles.map((item) => (
                        <li className="selected-file-item" key={item.id}>
                          <div className="file-type-pill">
                            <span>{item.name.split(".").pop().toUpperCase()}</span>
                          </div>
                          <div className="file-details">
                            <div className="file-top-row">
                              <span className="file-name" title={item.name}>
                                {item.name}
                              </span>
                              <Tag
                                color={item.status === "ready" ? "green" : "red"}
                                style={{ marginInlineEnd: 0 }}
                              >
                                {item.status === "ready" ? "Ready" : "Needs attention"}
                              </Tag>
                            </div>
                            <div className="file-meta">
                              <span>{formatFileSize(item.size)}</span>
                              {item.employerCode && (
                                <span>Employer code: {item.employerCode}</span>
                              )}
                              {item.languageLabel && (
                                <span>Language: {item.languageLabel}</span>
                              )}
                              {item.fileStamp && <span>Stamp: {item.fileStamp}</span>}
                            </div>
                            <div className="selected-file-tags">
                              {item.employerCode && <Tag>{item.employerCode}</Tag>}
                              {item.languageLabel && (
                                <Tag color={item.language === "english" ? "gold" : "blue"}>
                                  {item.languageLabel}
                                </Tag>
                              )}
                              {item.status === "ready" && (
                                <Tag color="green">Batch ready</Tag>
                              )}
                              {item.error && <Tag color="red">{item.error}</Tag>}
                            </div>
                          </div>
                          <button
                            type="button"
                            className="remove-file-btn"
                            aria-label={`Remove ${item.name}`}
                            onClick={() => removeFile(item.id)}
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="empty-files-state">
                      <p>No files selected yet.</p>
                      <span>Pick one or more Excel files to begin the batch.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="upload-step completed">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3 className="step-title">Batch Summary</h3>
                <p className="step-description">
                  Files with the same employer code and language are merged into a
                  single upload batch.
                </p>

                {invalidFiles.length > 0 && (
                  <div className="bulk-upload-warning">
                    Remove the invalid files first. This upload only runs when every
                    selected file is valid.
                  </div>
                )}

                <div className="batch-summary-list">
                  {batchSummaries.length ? (
                    batchSummaries.map((batch) => (
                      <div className="batch-summary-card" key={batch.batchKey}>
                        <div className="batch-summary-header">
                          <div className="batch-employer">
                            <img
                              src={batch.employerImage || DEFAULT_EMPLOYER_IMAGE}
                              alt={batch.employerName}
                              onError={(event) => {
                                event.target.src = DEFAULT_EMPLOYER_IMAGE;
                              }}
                            />
                            <div>
                              <h4>{batch.employerName}</h4>
                              <p>{batch.employerCode}</p>
                            </div>
                          </div>
                          <div className="batch-summary-badges">
                            <Tag color={batch.language === "english" ? "gold" : "blue"}>
                              {batch.languageLabel}
                            </Tag>
                            <Tag color="green">{batch.files.length} file(s)</Tag>
                          </div>
                        </div>

                        <div className="batch-summary-body">
                          <span>Files in this batch</span>
                          <div className="batch-file-list">
                            {batch.files.map((file) => (
                              <span className="batch-file-chip" key={file.id}>
                                {file.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-files-state">
                      <p>No ready batches yet.</p>
                      <span>
                        Upload at least one valid file with a recognized employer code
                        and language.
                      </span>
                    </div>
                  )}
                </div>

                <div className="bulk-upload-summary">
                  <div className="summary-copy">
                    <h3>Ready to upload</h3>
                    <p>
                      {batchSummaries.length} batch(s) will upload as {totalFiles} file(s) in one request.
                    </p>
                  </div>

                  <div className="summary-stats">
                    <div className="summary-stat">
                      <span>Files</span>
                      <strong>{selectedFiles.length}</strong>
                    </div>
                    <div className="summary-stat">
                      <span>Batches</span>
                      <strong>{batchSummaries.length}</strong>
                    </div>
                    <div className="summary-stat">
                      <span>Ready</span>
                      <strong>{readyFiles.length}</strong>
                    </div>
                  </div>
                </div>

                <div className="bulk-upload-actions">
                  <button
                    type="button"
                    className="bulk-clear-btn"
                    onClick={clearFiles}
                    disabled={!selectedFiles.length}
                  >
                    Clear Files
                  </button>
                  <button
                    type="button"
                    onClick={handleUpload}
                    className="submit-btn"
                    disabled={!canUpload}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M17 8L12 3L7 8"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M12 3V15"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Upload All Parsed Batches
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkOfferUpload;
